<template>
  <header
    class="bg-white shadow-sm border-b border-gray-200 sm:sticky sm:top-0 sm:z-40"
  >
    <div class="px-4 sm:px-6 py-3 sm:py-4">
      <div class="flex items-center justify-between gap-4 min-w-0">
        <!-- Mobile Actions -->
        <div
          class="flex-1 flex justify-center items-center space-x-4 sm:hidden ml-11"
        >
          <!-- Home (Mobile only) -->
          <router-link to="/">
            <button
              class="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors duration-200"
            >
              <Home class="w-6 h-6" />
            </button>
          </router-link>
          <Calender />

          <button
            @click="toggleNotifications"
            class="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full flex items-center justify-center min-h-10 min-w-10"
          >
            <Bell class="w-4 h-4" />
          </button>

          <RefreshButton />
        </div>

        <!-- Center Title (md+) -->
        <div
          class="flex-1 min-w-0 text-center md:text-left hidden md:block md:ml-[42px]"
        >
          <h2 class="text-base md:text-xl font-semibold text-gray-900 truncate">
            {{ getCurrentPageTitle() }}
          </h2>

          <p
            class="text-blue-800 truncate bg-blue-100 px-2.5 py-0.5 rounded-full text-xs font-medium w-fit mt-1"
          >
            {{ getCurrentPageDescription() }}
          </p>
        </div>

        <!-- Desktop Actions -->
        <div class="hidden sm:flex items-center space-x-4 ml-auto">
          <!-- Today Stats (XL only) -->
          <TodaysData class="hidden xl:block" />

          <!-- Calendar -->
          <Calender />

          <!-- Refresh -->
          <RefreshButton />

          <!-- Bell -->
          <button
            @click="toggleNotifications"
            class="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full flex items-center justify-center min-h-10 min-w-10"
          >
            <Bell class="w-5 h-5" />
            <span
              v-if="unreadCount > 0"
              class="absolute -top-0.5 -right-0.5 flex h-3 w-3"
            >
              <span
                class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"
              ></span>
              <span
                class="relative inline-flex rounded-full h-3 w-3 bg-red-500"
              ></span>
            </span>
          </button>
        </div>

        <!-- Profile -->
        <UserProfile />
      </div>
    </div>

    <!-- Notifications Popup -->
    <div
      v-if="showNotifications"
      class="fixed sm:absolute inset-x-4 top-20 sm:inset-auto sm:right-4 sm:top-full sm:mt-2 w-auto sm:w-96 max-h-[75vh] sm:max-h-[400px] overflow-y-auto bg-white rounded-xl shadow-2xl border border-gray-200 z-[10000]"
    >
      <div
        class="p-4 border-b flex items-center justify-between bg-blue-50 rounded-t-lg"
      >
        <h3 class="text-lg font-semibold text-gray-800">Notifications</h3>
        <button @click="showNotifications = false">
          <X class="w-5 h-5" />
        </button>
      </div>

      <div
        v-if="loading"
        class="p-6 text-center"
      >
        Loading...
      </div>

      <div
        v-else-if="notifications.length === 0"
        class="p-6 text-center"
      >
        No notifications.
      </div>

      <ul
        v-else
        class="divide-y"
      >
        <li
          v-for="n in notifications"
          :key="n.leaveId"
          @click="markAsRead(n.leaveId)"
          class="p-4 cursor-pointer transition rounded-lg mx-2 my-1"
          :class="getNotificationClass(n.type)"
        >
          <p class="text-sm font-medium">
            {{ n.message }}
          </p>

          <p class="text-xs mt-1 opacity-70">
            {{ formatTime(n.date) }}
          </p>
        </li>
      </ul>
    </div>

    <!-- Overlay -->
    <div
      v-if="showNotifications"
      @click="showNotifications = false"
      class="fixed inset-0 bg-black bg-opacity-25 z-[9999]"
    ></div>
  </header>
</template>

<script>
  import { Bell, X, Home } from "lucide-vue-next";
  import Calender from "./Calender/Calender.vue";
  import UserProfile from "./UserProfile/UserProfile .vue";
  import TodaysData from "./TodaysData/TodaysData.vue";
  import RefreshButton from "./Refresh/Refresh.vue";

  export default {
    name: "SchoolHeader",
    components: {
      TodaysData,
      Bell,
      UserProfile,
      Calender,
      RefreshButton,
      X,
      Home,
    },
    data() {
      return {
        showNotifications: false,
        loading: false,
      };
    },
    computed: {
      notifications() {
        return this.$store.state.notifications;
      },
      unreadCount() {
        return this.notifications.length;
      },
    },
    methods: {
      getNotificationClass(type) {
        switch (type) {
          case "leave_approved":
            return "bg-green-50 text-green-700 border border-green-200 hover:bg-green-100";

          case "leave_declined":
            return "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100";

          case "leave_applied":
          case "leave_application":
            return "bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100";

          default:
            return "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100";
        }
      },

      getCurrentPageTitle() {
        return "Welcome back 😊!";
      },
      getCurrentPageDescription() {
        return "what's happening at your school today.";
      },
      async fetchNotifications() {
        this.loading = true;
        await this.$store.dispatch("fetchNotifications");
        this.loading = false;
      },
      async toggleNotifications() {
        this.showNotifications = !this.showNotifications;

        if (this.showNotifications) {
          await this.fetchNotifications();
        }
      },
      markAsRead(notificationId) {
        const notification = this.notifications.find(
          (n) => n.leaveId === notificationId,
        );
        if (notification && !notification.read) {
          notification.read = true;
        }
      },

      formatTime(timestamp) {
        const date = new Date(timestamp);
        const now = new Date();
        const diffInHours = (now - date) / (1000 * 60 * 60);
        if (diffInHours < 1) return "Just now";
        if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`;
        return date.toLocaleDateString();
      },
    },
    mounted() {
      this.fetchNotifications();
    },
  };
</script>

<style scoped>
  @media (max-width: 640px) {
    /* sm breakpoint */
    .mobile-top-adjust {
      top: 83px !important;
    }
  }
</style>
